import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Brain, Loader2, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/model`;
const pct = (p) => (p == null ? '—' : `${Math.round(p * 100)}%`);
const NAMES = { nfl: 'NFL', cfb: 'College', nba: 'NBA' };
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'not yet');

const Segment = ({ s }) => {
  const up = s.factor > 1.005; const down = s.factor < 0.995;
  return (
    <p className="flex items-center gap-1.5 text-xs text-zinc-300">
      {up ? <TrendingUp className="w-3 h-3 text-emerald-400" /> : down ? <TrendingDown className="w-3 h-3 text-rose-400" /> : <span className="w-3" />}
      <b className="text-white">{s.label}</b> hit {pct(s.hit)} vs {pct(s.pred)} predicted <span className="text-zinc-500">({s.n})</span>
      <span className={`ml-auto font-mono ${up ? 'text-emerald-300' : down ? 'text-rose-300' : 'text-zinc-500'}`}>×{s.factor.toFixed(2)}</span>
    </p>
  );
};

const League = ({ lg, m }) => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-1.5" data-testid={`model-league-${lg}`}>
    <p className="text-sm font-bold text-white">{NAMES[lg]} <span className="text-[11px] font-normal text-zinc-500">· last trained {when(m.last?.[lg])}</span></p>
    {(m.segments?.[lg] || []).length ? m.segments[lg].map((s) => <Segment key={s.segment} s={s} />)
      : <p className="text-[11px] text-zinc-500">Learns once picks are graded (needs 12+ graded picks of a type before it adjusts).</p>}
    {(m.lessons?.[lg] || []).length > 0 && (
      <ul className="mt-1 space-y-0.5 rounded-md border border-violet-500/30 bg-violet-950/20 p-2" data-testid={`model-lessons-${lg}`}>
        {m.lessons[lg].map((l) => <li key={l} className="text-[11px] text-violet-100">• {l}</li>)}
      </ul>
    )}
  </div>
);

// What the picks model has learned from graded results, and how it re-weighted each pick type.
export default function ModelReport() {
  const [m, setM] = useState(null);
  const load = useCallback(() => axios.get(API).then((r) => setM(r.data)).catch(() => setM({})), []);
  useEffect(() => { load(); }, [load]);
  const retrain = async () => {
    try { await axios.post(`${API}/retrain`, { leagues: ['nfl', 'cfb', 'nba'] }); toast.success('Retraining — refresh in a minute'); setTimeout(load, 15000); } catch (e) { toast.error(e.response?.data?.detail || 'Retrain failed'); }
  };
  if (!m) return <div className="flex gap-2 text-zinc-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Loading model…</div>;
  return (
    <div className="rounded-xl border border-violet-500/30 bg-zinc-900/70 p-3 space-y-3" data-testid="model-report">
      <div className="flex items-center gap-2">
        <Brain className="w-5 h-5 text-violet-400" />
        <p className="text-sm font-bold text-white">What the model learned</p>
        <button type="button" onClick={retrain} disabled={m.training} data-testid="model-retrain"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-violet-500/50 px-2.5 py-1 text-[11px] font-bold text-violet-200 hover:bg-violet-500/10 disabled:opacity-50">
          <RefreshCw className={`w-3 h-3 ${m.training ? 'animate-spin' : ''}`} />{m.training ? 'Training…' : 'Retrain now'}
        </button>
      </div>
      <p className="text-[11px] text-zinc-500">Every graded pick feeds back in. NBA retrains nightly, NFL and College every Tuesday. Pick types that hit less than predicted get trimmed; ones that beat it get a small boost (×0.80 to ×1.08).</p>
      <div className="grid gap-3 md:grid-cols-3">{['nfl', 'cfb', 'nba'].map((lg) => <League key={lg} lg={lg} m={m} />)}</div>
      {(m.history || []).some((h) => h.changes?.length) && (
        <div className="space-y-0.5" data-testid="model-history">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Recent changes</p>
          {m.history.filter((h) => h.changes?.length).slice(0, 8).map((h) => (
            <p key={`${h.league}-${h.at}`} className="text-[11px] text-zinc-400">{when(h.at)} · {NAMES[h.league]}: {h.changes.map((c) => `${c.label} ×${c.from.toFixed(2)}→×${c.to.toFixed(2)}`).join(', ')}</p>
          ))}
        </div>
      )}
    </div>
  );
}
